import {Injectable} from '@angular/core';

export interface GeoStateEntry {
	name: string;
	latitude: number | null;
	longitude: number | null;
}

export interface GeoCountryEntry {
	name: string;
	latitude: number | null;
	longitude: number | null;
	states: GeoStateEntry[];
}

export interface GeoIndex {
	countries: Map<string, GeoCountryEntry>;
	statesByCountry: Map<string, Map<string, GeoStateEntry>>;
}

export interface GeoAliases {
	countries: Record<string, string>;
	states: Record<string, Record<string, string>>;
}

export interface GeoPoint {
	lat: number;
	lng: number;
}

@Injectable({
	providedIn: 'root'
})
export class GeoLocationService {
	private readonly geoUrl = 'assets/data/countries+states.json';
	private readonly aliasUrl = 'assets/data/geo-aliases.json';
	private indexRequest?: Promise<GeoIndex>;

	async getIndex(): Promise<GeoIndex> {
		if (!this.indexRequest) {
			this.indexRequest = Promise.all([
				fetch(this.geoUrl).then((response) => response.json() as Promise<GeoCountryEntry[]>),
				fetch(this.aliasUrl)
					.then((response) => response.json() as Promise<GeoAliases>)
					.catch(() => ({countries: {}, states: {}}))
			])
				.then(([countries, aliases]) => this.buildIndex(countries, aliases))
				.catch((error) => {
					this.indexRequest = undefined;
					throw error;
				});
		}
		return this.indexRequest;
	}

	resolveCoordinates(index: GeoIndex, country?: string, region?: string): GeoPoint | null {
		if (!country) {
			return null;
		}
		const countryKey = this.normalizeName(country);
		const countryEntry = index.countries.get(countryKey);
		if (!countryEntry) {
			return null;
		}
		if (region) {
			const regionKey = this.normalizeName(this.stripRegionSuffix(region));
			const stateMap = index.statesByCountry.get(countryKey);
			const stateEntry = stateMap?.get(regionKey);
			if (stateEntry && this.isValidCoordinate(stateEntry.latitude, stateEntry.longitude)) {
				return {lat: Number(stateEntry.latitude), lng: Number(stateEntry.longitude)};
			}
		}
		if (this.isValidCoordinate(countryEntry.latitude, countryEntry.longitude)) {
			return {lat: Number(countryEntry.latitude), lng: Number(countryEntry.longitude)};
		}
		return null;
	}

	private buildIndex(countries: GeoCountryEntry[], aliases: GeoAliases): GeoIndex {
		const countryMap = new Map<string, GeoCountryEntry>();
		const stateMapByCountry = new Map<string, Map<string, GeoStateEntry>>();
		countries.forEach((country) => {
			const countryKey = this.normalizeName(country.name);
			countryMap.set(countryKey, country);
			const stateMap = new Map<string, GeoStateEntry>();
			country.states?.forEach((state) => {
				const normalizedState = this.normalizeName(this.stripRegionSuffix(state.name));
				stateMap.set(normalizedState, state);
				stateMap.set(this.normalizeName(state.name), state);
			});
			stateMapByCountry.set(countryKey, stateMap);
		});
		this.applyAliases(countryMap, stateMapByCountry, aliases);
		return {
			countries: countryMap,
			statesByCountry: stateMapByCountry
		};
	}

	private applyAliases(
		countries: Map<string, GeoCountryEntry>,
		statesByCountry: Map<string, Map<string, GeoStateEntry>>,
		aliases: GeoAliases
	): void {
		Object.entries(aliases.countries ?? {}).forEach(([alias, canonical]) => {
			const canonicalKey = this.normalizeName(canonical);
			const canonicalEntry = countries.get(canonicalKey);
			if (!canonicalEntry) {
				return;
			}
			countries.set(this.normalizeName(alias), canonicalEntry);
		});

		Object.entries(aliases.states ?? {}).forEach(([countryName, stateAliases]) => {
			const countryKey = this.normalizeName(countryName);
			const stateMap = statesByCountry.get(countryKey);
			if (!stateMap) {
				return;
			}
			Object.entries(stateAliases).forEach(([alias, canonical]) => {
				const canonicalKey = this.normalizeName(this.stripRegionSuffix(canonical));
				const canonicalEntry = stateMap.get(canonicalKey) ?? stateMap.get(this.normalizeName(canonical));
				if (!canonicalEntry) {
					return;
				}
				stateMap.set(this.normalizeName(this.stripRegionSuffix(alias)), canonicalEntry);
				stateMap.set(this.normalizeName(alias), canonicalEntry);
			});
		});
	}

	private stripRegionSuffix(value: string): string {
		return value
			.replace(/\b(province|state|region|district|county|departement|département)\b/gi, '')
			.replace(/\s{2,}/g, ' ')
			.trim();
	}

	private normalizeName(value: string): string {
		return value
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.replace(/[^a-zA-Z0-9]+/g, ' ')
			.trim()
			.toLowerCase();
	}

	private isValidCoordinate(lat?: number | null, lng?: number | null): boolean {
		return typeof lat === 'number' && typeof lng === 'number' && !Number.isNaN(lat) && !Number.isNaN(lng);
	}
}
